import { distinctUntilChanged, Subject } from "rxjs";
import { Logger } from "../../02_utils/logger";
import { Utils } from "../../02_utils/utils";
import { BackdropHandler } from "./backdropHandler";


export class Navigation {

  private prevWindowWidth: number = window.innerWidth;
  private navigation: HTMLElement;
  private backdropHandler: BackdropHandler;
  private moreLinks: NodeListOf<HTMLAnchorElement>;
  private mobileNavigations: NodeListOf<HTMLElement>;
  private animationDuration: number = 250;
  private mobileWidth: number = 1080;

  constructor(navigation: HTMLElement, private $log: Logger, private $utils: Utils) {
    this.navigation = navigation;
    const backdrop = this.navigation.querySelector('.js-panel-backdrop') as HTMLElement;

    this.backdropHandler = new BackdropHandler(backdrop, $log, $utils);

    this.backdropHandler.backdropActive.pipe(distinctUntilChanged()).subscribe((active: boolean) => {
      if (!active) {
        this.closeNavigationLayers(true, null);
      }
    });

    this.moreLinks = this.navigation.querySelectorAll('.js-l-navigation__link');
    this.mobileNavigations = this.navigation.querySelectorAll('.js-mobile-navigation');

    this.handleLinks();
    this.handleCloseNavigation();

    document.addEventListener('base:navigation-close', () => {
      this.closeNavigationLayers(true);
    });

    window.addEventListener('resize', () => {
      this.handleMobileNav();
    }, {passive: true});

    this.handleMobileNav();
  }

  private handleMobileNav() {
    if (window.innerWidth < this.mobileWidth) {
      this.mobileNavigations?.forEach(mobileNavigation => {
        mobileNavigation.setAttribute('tabindex', '-1');
        mobileNavigation.setAttribute('aria-hidden', 'true');
      });
    } else {
      this.mobileNavigations?.forEach(mobileNavigation => {
        mobileNavigation.removeAttribute('tabindex');
        mobileNavigation.removeAttribute('aria-hidden');
      });
    }

    if (window.innerWidth > this.mobileWidth && this.prevWindowWidth <= this.mobileWidth || window.innerWidth < this.mobileWidth && this.prevWindowWidth >= this.mobileWidth) {
      this.closeNavigationLayers(true, null);
      document.dispatchEvent(new CustomEvent('closePanel'));
    }

    this.prevWindowWidth = window.innerWidth;
  }

  private handleLinks() {
    this.moreLinks?.forEach(link => {
      const pageId = link.dataset.pageId;
      const level = link.dataset.level;

      link.addEventListener('click', (event: Event) => {
        event.preventDefault();

        const active = this.isLevelActive(level);

        if (!link.classList.contains('open')) {
          this.closeNavigationLayers(false, level);
          this.openNavigationLayer((!active), pageId);

          this.removeOpenFromLinks(level);
          this.$utils.addOrRemoveClases('add', 'open', link);
        } else {
          this.closeNavigationLayers(true, null);
          this.removeOpenFromLinks(level);
        }
      });
    });

    document.addEventListener('ercas:close:navigation', () => {
      this.closeNavigationLayers(true, null);
    }, {passive: true});
  }

  private findOpenLevels(level: string): NodeListOf<HTMLElement> {
    let selector = '[data-parent-page-id].open';

    if (level) {
      selector += `[data-level="${level}"]`;
    }

    return this.navigation.querySelectorAll(selector);
  }

  private isLevelActive(level: string): boolean {
    const activeLevels = document.querySelector(`[data-level="${level}"][data-parent-page-id].open`);

    if (activeLevels) {
      return true;
    }

    return false;
  }

  public handleCloseNavigation(): void {
    const closeLinks: NodeListOf<HTMLElement> = this.navigation.querySelectorAll('.js-l-navigation-close');

    closeLinks?.forEach(closeLink => {
      closeLink.addEventListener('click', (event: Event) => {
        event.preventDefault();

        const level = closeLink.dataset.level;

        this.closeNavigationLayers(true, level);
        this.removeOpenFromLinks(level);
      });
    });

  }

  private openNavigationLayer(animate: boolean, id: string) {
    const navigationLayer: HTMLElement = this.navigation.querySelector(`[data-parent-page-id="${id}"]`);
    this.backdropHandler.backdropActive.next(true);

    navigationLayer.removeAttribute('aria-hidden');
    navigationLayer.setAttribute('tabindex', '0');
    navigationLayer.focus();

    if (animate == true) {
      this.handleAnimation(navigationLayer);
      this.$utils.addOrRemoveClases('add', 'open', navigationLayer);
    } else {
      this.$utils.addOrRemoveClases('add', 'open', navigationLayer);
    }
  }

  private closeNavigationLayers(animate: boolean, level: string = null) {
    const navigationLayers: NodeListOf<HTMLElement> = this.findOpenLevels(level);
    if (level == null) {
      this.backdropHandler.backdropActive.next(false);
    }

    navigationLayers?.forEach(navigationLayer => {
      navigationLayer.setAttribute('aria-hidden', 'true');
      navigationLayer.setAttribute('tabindex', '-1');
    });

    if (animate == true) {
      this.handleAnimation(navigationLayers);
      this.$utils.addOrRemoveClases('remove', 'open', navigationLayers);
    } else {
      this.$utils.addOrRemoveClases('remove', 'open', navigationLayers);
    }

    this.removeOpenFromLinks(level);
  }

  private removeOpenFromLinks(level: string = null): void {
    let selector = '.js-l-navigation__link';
    if (level) {
      selector += `[data-level="${level}"]`;
    }
    const moreLinks: NodeListOf<HTMLAnchorElement> = this.navigation.querySelectorAll(selector);

    this.$utils.addOrRemoveClases('remove', 'open', moreLinks);
  }

  private handleAnimation(element: NodeListOf<HTMLElement> | HTMLElement) {
    this.$utils.addOrRemoveClases('add', 'animate', element);

    setTimeout(() => {
      this.$utils.addOrRemoveClases('remove', 'animate', element);
    }, this.animationDuration);
  }
}
